import type { PostAttributes, WorldAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { PostEventType, PostScope, PostStatus, PostType } from '@/client/services/worlds'

import ChannelQ from './ChannelQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import LiveHelpRequestQ from './LiveHelpRequestQ'
import PostEventQ from './PostEventQ'
import PostFeedbackQ from './PostFeedbackQ'
import SeriesQ from './SeriesQ'
import SessionQ from './SessionQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const PostQ = {
  ...makeQueryModelV2<PostAttributes>('post')([
    'id',
    'type',
    'world_id',
    'channel_id',
    'session_id',
    'series_id',
    'user_id',
    'body',
    'pinned_scope',
    'status',
    'attachment_urls',
    'payload',
    'last_edited_at',
    'hidden_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Channel: {
      query: ChannelQ,
      foreignKey: ChannelQ.id,
      sourceKey: PostQ.channel_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: PostQ.user_id,
    },
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: PostQ.world_id,
    },
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: PostQ.session_id,
    },
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: PostQ.series_id,
    },
    PostEvents: {
      query: PostEventQ,
      foreignKey: PostEventQ.post_id,
      sourceKey: PostQ.id,
    },
    PostFeedbacks: {
      query: PostFeedbackQ,
      foreignKey: PostFeedbackQ.post_id,
      sourceKey: PostQ.id,
    },
  }),

  userCanViewPost: (userId: number) =>
    SQL`(
      (${WorldQ.hasVisibility('public')}
        OR ${WorldQ.isUserMember(userId)}) AND
      (${PostQ.channel_id} IS NULL
          OR ${ChannelQ.hasVisibility('public')}
          OR ${ChannelQ.isUserMember(userId)})
    )`,

  where: (
    {
      id,
      type,
      worldId,
      channelId,
      userId,
      pinnedScope,
      status,
      groupBy = null,
      include = [],
      order = null,
      limit = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      type?: PostType | null | SQLFragment<PostType>
      worldId?: number | null | SQLFragment<number>
      channelId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      pinnedScope?: PostScope
      status?: PostStatus
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
      include?: IncludeAssociations
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<PostAttributes> => SQL`
        FROM ${PostQ}
        ${includeAssociationsToSql(PostQ, include)}
        WHERE
            ${id != null ? SQL`${PostQ.id} = ${id} AND` : SQL``}
            ${type != null ? SQL`${PostQ.type} = ${type} AND` : SQL``}
            ${worldId != null ? SQL`${PostQ.world_id} = ${worldId} AND` : SQL``}
            ${channelId != null ? SQL`${PostQ.channel_id} = ${channelId} AND` : SQL``}
            ${userId != null ? SQL`${PostQ.user_id} = ${userId} AND` : SQL``}
            ${pinnedScope != null ? SQL`${PostQ.pinned_scope} = ${pinnedScope} AND` : SQL``}
            ${status != null ? SQL`${PostQ.status} = ${status} AND` : SQL``}
            ${paranoid ? SQL`${PostQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  countComments: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostEventQ.where({ postId: PostQ.id, type: 'comment' })}`,

  countPostsInLastWeek: (userId: number): SQLFragment<number> =>
    SQL`COUNT(*) ${PostQ.where(
      {
        userId,
        type: 'user-post',
      },
      SQL`${PostQ.created_at} > NOW() - INTERVAL '7 DAY'`
    )}`,

  getCurrentLiveHelpRequest: (): SQLFragment<PostAttributes> =>
    SQL`${LiveHelpRequestQ.where(
      {
        postId: PostQ.id,
        order: [[LiveHelpRequestQ.created_at, 'desc']],
        limit: 1,
      },
      SQL`
          ${LiveHelpRequestQ.created_at} = (
            SELECT MAX(${LiveHelpRequestQ.created_at}) ${LiveHelpRequestQ.where({
              postId: PostQ.id,
            })}
          ) AND
          ${LiveHelpRequestQ.completed_at} IS NULL AND
          ${LiveHelpRequestQ.canceled_at} IS NULL`
    )}`,

  getWorld: (): SQLFragment<WorldAttributes> => SQL`${WorldQ.where({ id: PostQ.world_id })}`,

  getChannel: (): SQLFragment<WorldAttributes> => SQL`${ChannelQ.where({ id: PostQ.channel_id })}`,

  firstPostWithNotHelpfulRating: ({ userId }: { userId: number }): SQLFragment<PostAttributes> =>
    SQL`FROM ${PostQ}
          JOIN ${PostFeedbackQ} ON ${PostQ.id} = ${PostFeedbackQ.post_id}
          WHERE ${PostFeedbackQ.user_id} = ${userId} AND ${PostFeedbackQ.rating} = 'Not Helpful'
        ORDER BY ${PostQ.created_at} ASC
        LIMIT 1`,

  getMedianResponseSeconds: ({
    channelId,
    worldId,
    timeframeDays,
    withinHours,
    include = [],
  }: {
    channelId?: number | SQLFragment<number>
    worldId?: number | SQLFragment<number>
    timeframeDays: number
    withinHours?: number
    include?: IncludeAssociations
  }): SQLFragment<number | null> => {
    const RESPONSE_POST_EVENT_TYPES: PostEventType[] = ['comment', 'open-live-help-session']

    const timeframeCondition = SQL`${PostQ.created_at} > NOW() - ${timeframeDays} * INTERVAL '1 DAY'`

    const withinHoursCondition =
      withinHours != null
        ? SQL`AND (
          ABS(EXTRACT(HOUR FROM ${PostQ.created_at}) - EXTRACT(HOUR FROM NOW())) <= ${withinHours}
          OR
          ABS(EXTRACT(HOUR FROM ${PostQ.created_at}) - EXTRACT(HOUR FROM NOW())) >= ${24 - withinHours}
        )`
        : SQL``

    return SQL`PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY EXTRACT(EPOCH FROM (${
      PostEventQ.created_at
    } - ${PostQ.created_at}))) ${PostQ.where(
      {
        channelId,
        worldId,
        type: 'user-post',
        include: [
          {
            association: 'PostEvents',
            required: true,
            where: SQL`${PostEventQ.created_at} = (SELECT MIN(${PostEventQ.created_at}) ${PostEventQ.where(
              {
                postId: PostQ.id,
              },
              SQL`${PostEventQ.user_id} != ${PostQ.user_id} AND ${PostEventQ.type} = ANY(${RESPONSE_POST_EVENT_TYPES})`
            )})`,
          },
          ...include,
        ],
      },
      SQL`${timeframeCondition} ${withinHoursCondition}`
    )}`
  },
}

export default PostQ
